import {first, last, partition, pipe} from 'remeda'
import {avgOf, max, min} from '../agg'
import {mapWithPrev} from '../collection'
import {DistLogistic} from './distributionLogistic'
const {abs, floor} = Math
import {Dist} from './distributions'

export namespace DistV1ForAgg {
  type MakeDiscretePdOptions = {ticks?: number}
  type AggDistributionOptions = {
    displayBounds?: [number, number]
    title?: string
  } & MakeDiscretePdOptions
  export const agg = (
    dists: Dist.DiscreteDistribution[],
    options?: AggDistributionOptions
  ): Dist.DiscreteDistribution => {
    const {displayBounds, ticks} = options ?? {}
    const fullRange: [number, number] = [
      min(dists.map(({pts: pd}) => first(pd)!.x)),
      max(dists.map(({pts: pd}) => last(pd)!.x))
    ]

    const range = Dist.createRange(fullRange, ticks ?? Dist.numTicks)
    // consistent x point across all distributions allows us to aggregate each on by pAcc
    const cdfPoints = range
      .map(x => ({
        x,
        pAcc: avgOf(
          dists.map(d => d.adjustedPt(x)),
          p => p.pAcc
        )
      }))
      .filter(p => p.pAcc != null) as {x: number; pAcc: number}[]

    // convert cdf to pdf

    const bounds = displayBounds ?? fullRange

    //  covert back to a cdf and filter out the points outside the display bounds
    const {pts, left, right} = pipe(withPdFromCd(cdfPoints), pts => {
      const [ptsDisplayed, ptsOutside] = partition(
        mapWithPrev(pts, (p, {prev, next}) => ({
          ...p,
          inBounds: (next?.x ?? p.x) >= bounds[0] && (prev?.x ?? p.x) <= bounds[1]
        })),
        p => p.inBounds
      )

      const getTail = (side: 'left' | 'right') => Dist.getTail({side, ptsDisplayed, pts, bounds, pdfs: dists})

      return {pts: ptsDisplayed, ptsOutside, left: getTail('left'), right: getTail('right')}
    })
    return Dist.withDiscreteFns({pts, left, right, bounds})
  }

  export type DiscretePd = {
    points: {p: number; x: number}[]
    tails?: {
      left?: number
      right?: number
    }
  }

  /** derives pAcc from p.
   * p is the probability at the point, and pAcc is the accumulated probability (0-1) including this one.
   * Now supports non uniform spacing of x */
  export const withCdFromPd = ({points, tails}: DiscretePd) => {
    let pAcc = 0
    const basicAcc = mapWithPrev(points, (pt, {prev, next}) => {
      if (!next && !prev) console.warn('withCdFromPd item with to prev or next')
      const dx = next ? next.x - pt.x : prev ? pt.x - prev!.x : 0 // calculate the width, for the last point assume it is the same with as previous as a convention to be able to round-trip cpd->cd->pd
      pAcc += pt.p * dx
      return {...pt, pAcc}
    })
    const toPct = (v?: number) => (v ? v / 100 : undefined)
    const leftPct = toPct(tails?.left) ?? 0
    const normalize = (1 - leftPct - (toPct(tails?.right) ?? 0)) / pAcc
    let res = basicAcc.map(pt => ({...pt, pAcc: leftPct + pt.pAcc * normalize}))

    if (res.length > 2) {
      const pLeft = first(res)!
      const pRight = last(res)!
      const width = pRight.x - pLeft.x
      // place the tail x at an arbitrary point (because it is undefined and outside out display bounds. in the future we will let the forecaster configure the shape of the tail to make this better)
      if (tails?.left) {
        res = [{x: pLeft.x - width, p: 0, pAcc: 0}, ...res]
      }
      if (tails?.right) {
        res = [...res, {x: pRight.x + width, p: 0, pAcc: 1}]
      }
    }

    return res
  }

  /** derives p from pAcc. Usefull if you have manipulated pAcc (e.g. aggregating) and then need to display p */
  export const withPdFromCd = (cd: Pick<Dist.Point, 'pAcc' | 'x'>[]) =>
    mapWithPrev(cd, (pt, {prev, next}) => {
      const dx = next ? next.x - pt.x : pt.x - prev!.x
      const p = dx == 0 ? 0 : (pt.pAcc - (prev?.pAcc ?? 0)) / dx
      return {...pt, p}
    })

  // /** creates a distribution given the cfg, you need to add labels explicitly.
  //  * Remember the bounds of cfg ar hard!
  //  */
  // export const fromLogistic = (cfg: DistLogistic.LogisticCfg, options?: MakeDiscretePdOptions) => {
  //   const pd = makeDiscretePd(cfg, options)
  //   return Dist.pointsToDiscrete(pd)
  // }

  const makeDiscretePd = (cfg: DistLogistic.LogisticCfg, options: MakeDiscretePdOptions = {}): Dist.Point[] => {
    const {location, scale, hardBounds: bounds} = {...DistLogistic.defaultLogistic, ...cfg}
    // find the range to generate a standard 0 location pd with the given skew.
    // It needs to be wider than the translated-to-real-points chart.
    const radius = DistLogistic.sdWidthsToGenerate * scale
    const range = Dist.createRange(
      [bounds?.min ?? location - radius, bounds?.max ?? location + radius],
      options?.ticks ?? Dist.numTicks,
      1
    )
    const pdf = DistLogistic.probabilityDensitySkewLogistic(cfg)
    const points = withCdFromPd({points: range.map(x => ({p: pdf(x), x}))})
    return points
  }
}
